(function () {
  'use strict';

  const EmblaCarousel = window.EmblaCarousel;
  const AutoHeight = window.EmblaCarouselAutoHeight;
  const Autoplay = window.EmblaCarouselAutoplay;
  const AutoScroll = window.EmblaCarouselAutoScroll;
  const ClassNames = window.EmblaCarouselClassNames;
  const { carouselLazyLoad } = window.Universe;

  const pendingCarousels = new Map(); // wrap → carousel
  const observedSections = new Set(); // .shopify-section elements currently observed
  let sharedObserver = null;

  function getSharedObserver() {
    if (!sharedObserver) {
      sharedObserver = new IntersectionObserver(
        (entries) => {
          for (let j = 0; j < entries.length; j++) {
            const entry = entries[j];
            if (entry.isIntersecting) {
              const section = entry.target;
              sharedObserver.unobserve(section);
              observedSections.delete(section);
              pendingCarousels.forEach((carousel, wrap) => {
                if (section.contains(wrap)) {
                  pendingCarousels.delete(wrap);
                  carousel.setup();
                }
              });
            }
          }
        },
        { rootMargin: '200px' },
      );
    }
    return sharedObserver;
  }

  class BaseCarousel {
    constructor(options) {
      this.init(options);
    }

    setup() {
      const plugins = [ClassNames()];
      if (this.withAutoScroll) {
        plugins.push(AutoScroll({ playOnInit: false, speed: this.autoScrollSpeed }));
      }
      if (this.withAutoPlay) {
        plugins.push(Autoplay({ playOnInit: this.autoPlayOnInit, delay: this.autoPlayDelay }));
      }
      if (this.withAutoHeight) {
        plugins.push(AutoHeight());
      }

      this.instance = EmblaCarousel(this.DOM.carousel, Object.assign({}, this.carouselOptions), plugins);

      if (this.DOM.images.length) {
        this.loadImagesInView = carouselLazyLoad(this.instance);
        this.instance.on('init', this.loadImagesInView);
        this.instance.on('select', this.loadImagesInView);
        this.instance.on('resize', this.loadImagesInView);
      }

      if (this.onReady) {
        this.onReady(this);
      }
    }

    // Returns true when the carousel lives in the first content section of the
    // page (above the fold), so it should skip the IntersectionObserver and
    // initialize immediately.
    //
    // Detection order:
    //  1. Explicit data-section-index attribute on the wrap or any ancestor
    //     (set in Liquid via `section.index` for hero-style sections).
    //  2. DOM position fallback: first .shopify-section inside <main>.
    _isSectionAtf(el) {
      const attrEl = el.closest('[data-section-index]');
      if (attrEl) return parseInt(attrEl.dataset.sectionIndex, 10) === 1;

      const section = el.closest('main .shopify-section');
      if (!section) return false;
      return document.querySelector('main .shopify-section') === section;
    }

    destroy() {
      if (this.DOM) {
        if (pendingCarousels.has(this.DOM.wrap)) {
          pendingCarousels.delete(this.DOM.wrap);
          const section = this.DOM.wrap.closest('.shopify-section') || this.DOM.wrap;
          if (observedSections.has(section)) {
            let stillPending = false;
            pendingCarousels.forEach((_, wrap) => { if (section.contains(wrap)) stillPending = true; });
            if (!stillPending) {
              getSharedObserver().unobserve(section);
              observedSections.delete(section);
            }
          }
        }
        if (this.instance) {
          if (this.DOM.images.length) {
            this.instance.off('init', this.loadImagesInView);
            this.instance.off('select', this.loadImagesInView);
            this.instance.off('resize', this.loadImagesInView);
          }
          this.instance.destroy();
        }

        this.instance = undefined;
        this.DOM = undefined;
      }
    }

    init(options) {
      if (options.wrap !== undefined) {
        this.DOM = {};
        this.DOM.wrap = options.wrap;
        this.DOM.carousel = options.carousel || options.wrap.querySelector('.js-carousel');
        this.DOM.images = this.DOM.wrap.querySelectorAll('.js-carousel-image');

        this.carouselOptions = options.carouselOptions || { align: 'start', containScroll: 'trimSnaps' };
        this.withAutoScroll = options.withAutoScroll || false;
        this.withAutoPlay = options.withAutoPlay || false;
        this.withAutoHeight = options.withAutoHeight || false;
        this.autoPlayOnInit = options.autoPlayOnInit !== undefined ? options.autoPlayOnInit : true;
        this.autoPlayDelay = options.autoPlayDelay || 4000;
        this.autoScrollSpeed = options.autoScrollSpeed || 3;
        this.onReady = options.onReady || null;
        this.instance = undefined;

        // ATF carousels (section index 1) initialize on the next microtask so
        // the parent's assignment (e.g. `this.instance = new BaseCarousel(...)`)
        // completes before onReady fires. All others wait for IntersectionObserver.
        if (this._isSectionAtf(options.wrap)) {
          Promise.resolve().then(() => {
            if (this.DOM) this.setup();
          });
        } else {
          pendingCarousels.set(this.DOM.wrap, this);
          const section = this.DOM.wrap.closest('.shopify-section') || this.DOM.wrap;
          if (!observedSections.has(section)) {
            observedSections.add(section);
            getSharedObserver().observe(section);
          }
        }
      }
    }
  }

  window.Universe.BaseCarousel = BaseCarousel;
})();
